// Server calls (Cloud Run via Hosting rewrite /api/**). Bearer-token only.
// Firestore reads never touch this — only scrape/extract/geocode do, so the
// cold-start hot path stays browser-direct.
import { auth } from './firebase.js';

export async function apiCall(path, { method = 'GET', body } = {}) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).detail || ''; } catch { /* non-JSON error body */ }
    throw new Error(`API ${res.status} ${detail}`.trim());
  }
  return res.status === 204 ? null : res.json();
}

/** Scrape the property's listing URL (pure compute; SPA persists the result). */
export const scrapeProperty = (pid) => apiCall(`/properties/${pid}/scrape`, { method: 'POST' });

/** Geocode an address string (Nominatim; pure compute). */
export const geocode = (address) => apiCall('/geocode', { method: 'POST', body: { address } });

/** Run free enrichment (homes estimate, suburb rents, OSM amenities). */
export const enrichProperty = (pid) => apiCall(`/properties/${pid}/enrich`, { method: 'POST' });

/** Start the paid property-files extraction for an owned property. */
export const extractProperty = (pid) => apiCall(`/properties/${pid}/extract`, { method: 'POST' });

/** Signed URLs for every file on the property (rendered as native links). */
export const fileUrls = (pid) => apiCall(`/properties/${pid}/files`);

/** Signed URL for one file (staleness refresh). */
export const fileUrl = (pid, idx) => apiCall(`/properties/${pid}/files/${idx}`);

/** Attach a user-uploaded file (drag-drop / button) to an owned property.
 *  Multipart — the browser sets the boundary, so no JSON Content-Type. */
export async function uploadPropertyFile(pid, file) {
  const token = await auth.currentUser.getIdToken();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api/properties/${pid}/files`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).detail || ''; } catch { /* non-JSON error body */ }
    throw new Error(`Upload ${res.status} ${detail}`.trim());
  }
  return res.json();
}
